import { Dialog, IconButton, Text, Tooltip, Table, ScrollArea, Flex, Button } from '@radix-ui/themes';
import { History } from 'lucide-react';
import { format } from 'date-fns';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

const GET_USER_CHANGE_LOG = gql`
  query GetUserChangeLog {
    viewer {
      client {
        members {
          nodes {
            id
            changeLog {
              date
              by
              changes {
                field
                oldValue
                newValue
              }
            }
          }
        }
      }
    }
  }
`;

type ChangeLogItem = {
  date: string;
  by: string | null;
  changes: {
    field: string;
    oldValue?: string;
    newValue?: string;
  }[];
};

const UserChangeLog = ({ user }: { user: { id: string; firstName: string; lastName: string } }) => {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useQuery(GET_USER_CHANGE_LOG, {
    fetchPolicy: 'network-only',
  });

  let changeLog: ChangeLogItem[] = [];

  if (data?.viewer?.client?.members?.nodes) {
    const member = data.viewer.client.members.nodes.find(
      (n: { id: string; changeLog: ChangeLogItem[] }) => n.id === user.id,
    );
    if (member && member.changeLog) {
      changeLog = member.changeLog;
    }
  }

  const formatValue = (value: string | undefined) => {
    if (!value) return null;
    return value.split('\n').map((line, i) => {
      if (line.startsWith('__KEY__')) {
        const key = line.replace('__KEY__', '');
        return (
          <div key={i} className="font-bold mt-2 first:mt-0">
            {t(`settings.organization.userChangeLog.fields.${key}`)}
          </div>
        );
      }
      return <div key={i}>{line}</div>;
    });
  };

  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (open) refetch();
      }}
    >
      <Tooltip content={<Text size="3">{t('settings.organization.userChangeLog.tooltip')}</Text>}>
        <Dialog.Trigger>
          <IconButton variant="ghost" color="gray" highContrast className="m-0">
            <History size={20} />
          </IconButton>
        </Dialog.Trigger>
      </Tooltip>
      <Dialog.Content style={{ maxWidth: 800, width: '90vw' }}>
        <Dialog.Title>
          {t('settings.organization.userChangeLog.dialogTitle', {
            userName: `${user.firstName} ${user.lastName}`,
          })}
        </Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {t('settings.organization.userChangeLog.description')}
        </Dialog.Description>

        <ScrollArea type="auto" scrollbars="vertical" style={{ maxHeight: 600 }}>
          {loading ? (
            <Flex justify="center" align="center" style={{ height: '100%' }}>
              <Text>{t('settings.organization.userChangeLog.loading')}</Text>
            </Flex>
          ) : error ? (
            <Flex justify="center" align="center" style={{ height: '100%' }}>
              <Text color="red">{t('settings.organization.userChangeLog.error')}</Text>
            </Flex>
          ) : changeLog.length === 0 ? (
            <Flex justify="center" align="center" style={{ height: '100%' }}>
              <Text color="gray">{t('settings.organization.userChangeLog.empty')}</Text>
            </Flex>
          ) : (
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>
                    {t('settings.organization.userChangeLog.columns.date')}
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    {t('settings.organization.userChangeLog.columns.changedBy')}
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    {t('settings.organization.userChangeLog.columns.changes')}
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {changeLog.map((log, index) => (
                  <Table.Row key={`log-${index}`}>
                    <Table.Cell>{format(new Date(log.date), 'yyyy-MM-dd HH:mm')}</Table.Cell>
                    <Table.Cell>{log.by || t('settings.organization.userChangeLog.systemUser')}</Table.Cell>
                    <Table.Cell>
                      <ul className="list-disc pl-4 m-0 text-sm">
                        {log.changes.map((change, cIndex) => (
                          <li key={`change-${index}-${cIndex}`}>
                            <strong>{t(`settings.organization.userChangeLog.fields.${change.field}`)}</strong>:
                            <div className="whitespace-pre-wrap mt-1 text-gray-700">
                              {change.oldValue ? (
                                <>
                                  {change.field === 'clientAccessLevel' ? (
                                    <>
                                      {t(`settings.organization.accessLevel.${change.oldValue}`)} &rarr;{' '}
                                      {t(`settings.organization.accessLevel.${change.newValue}`)}
                                    </>
                                  ) : (
                                    <>
                                      {change.oldValue} &rarr; {change.newValue}
                                    </>
                                  )}
                                </>
                              ) : (
                                <>{formatValue(change.newValue)}</>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </ScrollArea>

        <Flex gap="3" mt="4" justify="start">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              {t('settings.organization.userChangeLog.closeButton')}
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default UserChangeLog;
